import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { logError } from "../lib/logger";
import { AdminLayout } from "../components/AdminLayout";
import { FormProgress } from "../components/FormProgress";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, Users2, Crown, Home, MapPin, CalendarDays, Trash2 } from "lucide-react";
import { ROLE_LABEL } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { titleCase } from "../lib/textCase";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import { MultiMarkerMap } from "../components/MultiMarkerMap";
import { MoveClientDialog } from "../components/MoveClientDialog";
import { borrowedUntilLabel } from "../lib/teamMove";
import { CollabViewDialog } from "./Collaborators";
import { FormField } from "../components/ui/form-field";
import { SearchWithRecent } from "../components/SearchWithRecent";

const COLORS = [
  { name: "Azul", value: "#2563EB" },
  { name: "Verde", value: "#10B981" },
  { name: "Roxo", value: "#7C3AED" },
  { name: "Rosa", value: "#EC4899" },
  { name: "Laranja", value: "#F59E0B" },
  { name: "Vermelho", value: "#EF4444" },
  { name: "Turquesa", value: "#06B6D4" },
  { name: "Lima", value: "#84CC16" },
  { name: "Grafite", value: "#475569" },
];

const empty = {
  name: "",
  leader_id: null,
  member_ids: [],
  client_ids: [],
  color: COLORS[0].value,
  equipments: [],
};

export default function Teams() {
  const { t: tr } = useI18n();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [viewingLeader, setViewingLeader] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mapOf, setMapOf] = useState(null);
  const [scheduleOf, setScheduleOf] = useState(null);
  const { confirm, ConfirmModal } = useConfirm();

  const load = useCallback(async () => {
    const [t, u, c, e] = await Promise.all([
      api.get("/teams"),
      api.get("/collaborators"),
      api.get("/clients"),
      api.get("/equipments"),
    ]);
    setTeams(t.data);
    setUsers(u.data);
    setClients(c.data);
    setEquipments(e.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function remove(team) {
    let futureCount = 0;
    try {
      const { data } = await api.get(`/teams/${team.id}/future-jobs`);
      futureCount = data?.count || 0;
    } catch (e) {
      logError("Teams: falha ao contar agendamentos futuros", e);
    }
    const hasFuture = futureCount > 0;
    const ok = await confirm({
      title: tr('Excluir a equipe "{name}"?', { name: team.name }),
      description: hasFuture
        ? tr(
            "Esta equipe tem {n} agendamento(s) futuro(s). Eles serão removidos junto para não virarem agendamentos órfãos na agenda. Os equipamentos voltam ao estoque e as limpezas finalizadas ficam no histórico.",
            { n: futureCount },
          )
        : tr(
            "Os equipamentos atribuídos voltarão para o estoque. Esta ação não pode ser desfeita.",
          ),
      actionLabel: hasFuture
        ? tr("Excluir equipe e {n} agendamento(s)", { n: futureCount })
        : tr("Excluir equipe"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/teams/${team.id}${hasFuture ? "?remove_jobs=true" : ""}`);
      toast.success(
        hasFuture
          ? tr("Equipe e {n} agendamento(s) excluídos", { n: futureCount })
          : tr("Equipe excluída"),
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {tr("Estrutura")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{tr("Equipes")}</h1>
        </div>
        <Button
          data-testid="team-new-btn"
          onClick={() => {
            setEditing({ ...empty });
            setOpen(true);
          }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus size={16} className="mr-1" /> {tr("Nova equipe")}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {teams.map((t) => {
          const leader = users.find((u) => u.id === t.leader_id);
          return (
          <div
            className="cm-card overflow-hidden flex flex-col"
            key={t.id}
            data-testid={`team-card-${t.id}`}
          >
            <div
              className="h-1.5"
              style={{ background: t.color || "#2563EB" }}
            />
            <div className="p-5 flex flex-col flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ background: t.color || "#2563EB" }}
                  />
                  <InlineEditName
                    team={t}
                    onSaved={load}
                    testId={`team-rename-${t.id}`}
                  />
                  {/^Equipe ?\d+$/i.test((t.name || "").trim()) && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium"
                      title={tr("Considere renomear para algo memorável")}
                    >
                      {tr("Renomear")}
                    </span>
                  )}
                  {t.day_complete && (
                    <span
                      data-testid={`team-day-complete-${t.id}`}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold"
                    >
                      {tr("✓ Dia concluído")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Crown size={12} className="text-amber-500" />
                  {tr("Líder:")}{" "}
                  {users.find((u) => u.id === t.leader_id)?.name || "—"}
                </div>
                {(t.today_total || 0) > 0 && (
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">
                      {tr("Hoje")}: <strong className="text-slate-800">{t.today_finished}/{t.today_total}</strong> {tr("casas")}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex gap-1.5">
                <button
                  onClick={() => setMapOf(t)}
                  data-testid={`team-map-${t.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                  title={tr("Ver casas no mapa")}
                >
                  <MapPin size={18} />
                </button>
                <button
                  onClick={() => setScheduleOf(t)}
                  data-testid={`team-schedule-${t.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                  title={tr("Schedule semanal da equipe")}
                >
                  <CalendarDays size={18} />
                </button>
                <button
                  onClick={() => {
                    setEditing(t);
                    setOpen(true);
                  }}
                  data-testid={`team-edit-${t.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  title={tr("Editar equipe")}
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => remove(t)}
                  data-testid={`team-delete-${t.id}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                  title={tr("Excluir equipe")}
                >
                  <Trash2 size={18} />
                </button>
                </div>
                {/* Foto do líder — pequena, logo abaixo dos botões de ação */}
                <div className="flex flex-col items-center gap-0.5">
                  <div
                    className={`w-14 h-14 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 ${
                      leader ? "cursor-pointer group relative" : ""
                    }`}
                    data-testid={`team-leader-photo-${t.id}`}
                    onClick={leader ? () => setViewingLeader(leader) : undefined}
                    title={leader ? tr("Ver perfil do líder") : undefined}
                  >
                    {leader?.photo_path ? (
                      <img
                        src={fileUrl(leader.photo_path)}
                        alt={leader.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-amber-400">
                        <Crown size={22} />
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                    {tr("Líder")}
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
                {tr("Membros")} ({t.member_ids?.length || 0})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(t.member_ids || []).map((id) => {
                  const u = users.find((x) => x.id === id);
                  if (!u) return null;
                  return (
                    <span
                      key={id}
                      className="px-2.5 py-1 text-xs rounded-full bg-slate-100 text-slate-700"
                    >
                      {u.name} · {tr(ROLE_LABEL[u.role])?.slice(0, 4)}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
                <Home size={11} /> {tr("Casas atribuídas")} ({t.client_ids?.length || 0})
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(t.client_ids || []).slice(0, 6).map((id) => {
                  const c = clients.find((x) => x.id === id);
                  if (!c) return null;
                  const borrowed = borrowedUntilLabel(c);
                  return (
                    <span
                      key={id}
                      className={`px-2.5 py-1 text-xs rounded-full border ${
                        borrowed
                          ? "bg-amber-50 text-amber-800 border-amber-200"
                          : "bg-blue-50 text-blue-700 border-blue-100"
                      }`}
                      title={borrowed ? `Emprestada até ${borrowed}` : undefined}
                    >
                      {c.name}
                      {borrowed && (
                        <span className="ml-1 font-semibold">
                          · emprestada até {borrowed}
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
            {t.equipments && t.equipments.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-1">
                  📦 {tr("Equipamentos")} ({t.equipments.length})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {t.equipments.map((e) => {
                    const eq = equipments.find((x) => x.id === e.equipment_id);
                    if (!eq) return null;
                    return (
                      <span
                        key={e.equipment_id}
                        className="px-2.5 py-1 text-xs rounded-full bg-amber-50 text-amber-800 border border-amber-200 font-mono"
                      >
                        {eq.name} ×{e.quantity}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Foto do líder — pequena, logo abaixo dos botões de ação */}
            </div>
          </div>
          );
        })}
        {teams.length === 0 && (
          <div className="cm-card p-10 text-center text-slate-500 col-span-full">
            {tr("Nenhuma equipe cadastrada.")}
          </div>
        )}
      </div>

      <TeamDialog
        open={open}
        onOpenChange={setOpen}
        item={editing}
        users={users}
        clients={clients}
        equipments={equipments}
        teams={teams}
        onSaved={() => {
          setOpen(false);
          load();
        }}
      />
      <TeamMapDialog team={mapOf} clients={clients} onClose={() => setMapOf(null)} />
      <TeamScheduleDialog team={scheduleOf} users={users} onClose={() => setScheduleOf(null)} />
      <CollabViewDialog user={viewingLeader} onClose={() => setViewingLeader(null)} />
      {ConfirmModal}
    </AdminLayout>
  );
}

function TeamMapDialog({ team, clients, onClose }) {
  const { t: tr } = useI18n();
  const markers = React.useMemo(() => {
    if (!team) return [];
    return (team.client_ids || [])
      .map((cid) => clients.find((c) => c.id === cid))
      .filter((c) => c && c.lat && c.lng)
      .map((c) => ({
        id: c.id,
        lat: c.lat,
        lng: c.lng,
        color: team.color || "#2563EB",
        popupHTML: `<div style="font-size:13px;min-width:180px">
          <div style="font-weight:600">${c.name || ""}</div>
          <div style="font-size:11px;color:#64748B">${c.address || ""}</div>
          <div style="font-size:11px;color:#64748B">${[c.city, c.state, c.zip_code].filter(Boolean).join(", ")}</div>
          <div style="margin-top:6px;font-size:11px"><strong>${c.frequency || "—"}</strong> · US$ ${Number(c.value || 0).toFixed(2)}</div>
        </div>`,
      }));
  }, [team, clients]);

  if (!team) return null;
  const total = team.client_ids?.length || 0;
  const withGps = markers.length;

  return (
    <Dialog open={!!team} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: team.color || "#2563EB" }}
            />
            {tr("Mapa de casas —")} {team.name}
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-2">
          {withGps} {tr("de")} {total} {tr("casas com GPS capturado")}
        </div>
        {withGps === 0 ? (
          <div className="text-center py-10 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
            {tr("Nenhuma casa desta equipe tem GPS capturado. Edite os clientes e recapture o endereço pelo autocomplete.")}
          </div>
        ) : (
          <TeamMapView markers={markers} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TeamMapView({ markers }) {
  return <MultiMarkerMap markers={markers} height="500px" />;
}

const WEEKDAYS_FULL = [
  { id: 0, short: "Dom", label: "Domingo" },
  { id: 1, short: "Seg", label: "Segunda" },
  { id: 2, short: "Ter", label: "Terça" },
  { id: 3, short: "Qua", label: "Quarta" },
  { id: 4, short: "Qui", label: "Quinta" },
  { id: 5, short: "Sex", label: "Sexta" },
  { id: 6, short: "Sáb", label: "Sábado" },
];

function TeamScheduleDialog({ team, users, onClose }) {
  const { t: tr } = useI18n();
  if (!team) return null;
  const members = (team.member_ids || [])
    .map((uid) => users.find((u) => u.id === uid))
    .filter(Boolean);
  const today = new Date().getDay();

  return (
    <Dialog open={!!team} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: team.color || "#2563EB" }}
            />
            {tr("Schedule semanal —")} {team.name}
          </DialogTitle>
          <DialogDescription>
            {members.length} {tr("membros")} · {tr("Quem trabalha em cada dia da semana")}
          </DialogDescription>
        </DialogHeader>

        {members.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            {tr("Esta equipe ainda não tem membros atribuídos.")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {WEEKDAYS_FULL.map((day) => {
              const working = members.filter((u) =>
                (u.workdays || []).includes(day.id),
              );
              const isToday = today === day.id;
              return (
                <div
                  key={day.id}
                  data-testid={`team-schedule-day-${day.id}`}
                  className={`rounded-xl border-2 p-3 ${
                    isToday
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 bg-slate-50"
                  }`}
                >
                  <div
                    className={`text-xs font-bold uppercase tracking-wide mb-2 ${isToday ? "text-blue-700" : "text-slate-500"}`}
                  >
                    {tr(day.label)}
                    {isToday && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-600 text-white">
                        {tr("hoje")}
                      </span>
                    )}
                  </div>
                  {working.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">{tr("Folga")}</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {working.map((u) => (
                        <li
                          key={u.id}
                          className="text-xs flex items-center gap-1.5 truncate"
                        >
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="truncate text-slate-700">
                            {u.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 text-[10px] text-slate-400">
                    {working.length}{" "}
                    {working.length === 1 ? tr("pessoa") : tr("pessoas")}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TeamDialog({ open, onOpenChange, item, users, clients, equipments, teams, onSaved }) {
  const { t: tr } = useI18n();
  const [form, setForm] = useState(item || empty);
  const [memberSearch, setMemberSearch] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [moveClient, setMoveClient] = useState(null);
  // Mapa: clientId -> nome da equipe que já é dona (para oferecer "Mover p/ cá")
  const ownerByClient = {};
  (teams || []).forEach((tm) => {
    (tm.client_ids || []).forEach((cid) => {
      ownerByClient[cid] = { id: tm.id, name: tm.name };
    });
  });
  useEffect(() => {
    if (!open) return;
    if (item?.id) {
      setForm(item);
      return;
    }
    // Nova equipe → escolhe a primeira cor ainda não usada por outra equipe.
    const usedColors = new Set(
      (teams || [])
        .map((t) => (t.color || "").toLowerCase())
        .filter(Boolean),
    );
    const firstFree = COLORS.find((c) => !usedColors.has(c.value.toLowerCase()));
    setForm({ ...empty, color: (firstFree || COLORS[0]).value });
  }, [item, open, teams]);

  function toggle(arr, id) {
    return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
  }
  function set(k, v) {
    const val = k === "name" ? titleCase(v) : v;
    setForm((f) => ({ ...f, [k]: val }));
  }

  const byName = (a, b) =>
    (a.name || "").localeCompare(b.name || "", "pt", { sensitivity: "base" });
  const mq = memberSearch.trim().toLowerCase();
  const filteredMembers = [...users]
    .sort(byName)
    .filter((u) => !mq || (u.name || "").toLowerCase().includes(mq));
  const cq = clientSearch.trim().toLowerCase();
  const filteredClients = [...clients]
    .sort(byName)
    .filter(
      (c) =>
        !cq ||
        (c.name || "").toLowerCase().includes(cq) ||
        (c.address || "").toLowerCase().includes(cq),
    );

  async function save(force = false) {
    try {
      const qs = force ? "?force=true" : "";
      if (item?.id) await api.patch(`/teams/${item.id}${qs}`, form);
      else await api.post(`/teams${qs}`, form);
      toast.success(force ? tr("Movido para esta equipe") : tr("Equipe salva"));
      onSaved();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      if (status === 409 && !force) {
        // Conflito: casa/colaborador já está em outra equipe. Oferece transferir.
        toast.error(detail, {
          duration: 12000,
          action: {
            label: tr("Mover para esta equipe"),
            onClick: () => save(true),
          },
        });
      } else {
        toast.error(detail);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="team-dialog"
      >
        <DialogHeader>
          <DialogTitle>{item?.id ? tr("Editar equipe") : tr("Nova equipe")}</DialogTitle>
        </DialogHeader>

        <TeamFormProgress form={form} />

        <div className="space-y-4">
          <FormField label={tr("Nome da Equipe")}>
            
            <input
              data-testid="team-field-name"
              className="cm-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
</FormField>

          <div>
            <div className="text-sm font-medium text-slate-700 mb-2">
              {tr("Cor da equipe")}
            </div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => {
                const selected = (form.color || COLORS[0].value) === c.value;
                const usedBy = (teams || []).find(
                  (t) =>
                    t.id !== (item?.id || null) &&
                    (t.color || "").toLowerCase() === c.value.toLowerCase(),
                );
                const taken = !!usedBy && !selected;
                return (
                  <button
                    type="button"
                    key={c.value}
                    data-testid={`team-color-${c.value.replace("#", "")}`}
                    onClick={() => !taken && set("color", c.value)}
                    disabled={taken}
                    title={taken ? `${tr("Já usada por")} ${usedBy.name}` : tr(c.name)}
                    className={`relative w-9 h-9 rounded-full transition-transform ${
                      selected
                        ? "ring-2 ring-offset-2 ring-slate-900 scale-110"
                        : taken
                          ? "opacity-30 cursor-not-allowed"
                          : "hover:scale-105"
                    }`}
                    style={{ background: c.value }}
                  >
                    {taken && (
                      <span className="absolute inset-0 flex items-center justify-center text-white text-base font-bold">
                        ✕
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="text-xs text-slate-500 mt-2">
              Cor selecionada:{" "}
              <span
                className="font-mono px-1.5 py-0.5 rounded text-white text-[11px]"
                style={{ background: form.color || COLORS[0].value }}
              >
                {form.color || COLORS[0].value}
              </span>
              <span className="ml-2 text-slate-400">
                {tr("· cores com ✕ já estão em uso por outra equipe")}
              </span>
            </div>
          </div>

          <FormField label={tr("Líder")}>
            
            <select
              data-testid="team-field-leader"
              className="cm-input"
              value={form.leader_id || ""}
              onChange={(e) => set("leader_id", e.target.value || null)}
            >
              <option value="">{tr("— escolher —")}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
</FormField>

          <div>
            <div className="text-sm font-medium text-slate-700 mb-1">
              {tr("Membros")}
            </div>
            <input
              type="text"
              data-testid="team-member-search"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder={tr("Buscar membro…")}
              className="cm-input mb-2 text-sm"
            />
            <div className="border border-slate-200 rounded-lg p-2 max-h-44 overflow-y-auto">
              {filteredMembers.length === 0 && (
                <div className="text-xs text-slate-400 p-2 text-center">
                  {tr("Nenhum membro encontrado")}
                </div>
              )}
              {filteredMembers.map((u) => (
                <label
                  key={u.id}
                  className="flex items-center gap-2 py-1 text-sm cursor-pointer"
                >
                  <input
                    type="checkbox"
                    data-testid={`team-member-${u.id}`}
                    checked={form.member_ids.includes(u.id)}
                    onChange={() =>
                      set("member_ids", toggle(form.member_ids, u.id))
                    }
                  />
                  {u.name}
                  <span className="text-xs text-slate-500">
                    · {ROLE_LABEL[u.role]}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-slate-700 mb-1">
              {tr("Casas atribuídas")}
            </div>
            <input
              type="text"
              data-testid="team-client-search"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder={tr("Buscar casa por nome ou endereço…")}
              className="cm-input mb-2 text-sm"
            />
            <div className="border border-slate-200 rounded-lg p-2 max-h-44 overflow-y-auto">
              {filteredClients.length === 0 && (
                <div className="text-xs text-slate-400 p-2 text-center">
                  {tr("Nenhuma casa encontrada")}
                </div>
              )}
              {filteredClients.map((c) => {
                const owner = ownerByClient[c.id];
                const ownedByOther =
                  owner && item?.id && owner.id !== item.id;
                return (
                <div
                  key={c.id}
                  className="flex items-center justify-between gap-2 py-1"
                >
                  <label className="flex items-center gap-2 text-sm cursor-pointer min-w-0">
                    <input
                      type="checkbox"
                      data-testid={`team-client-${c.id}`}
                      checked={form.client_ids.includes(c.id)}
                      onChange={() =>
                        set("client_ids", toggle(form.client_ids, c.id))
                      }
                    />
                    <span className="truncate">
                      {c.name}
                      <span className="text-xs text-slate-500"> · {c.address}</span>
                    </span>
                  </label>
                  {ownedByOther && (
                    <button
                      type="button"
                      data-testid={`team-client-move-${c.id}`}
                      onClick={() => setMoveClient(c)}
                      title={`${tr("Está na")} ${owner.name} — ${tr("mover para cá")}`}
                      className="flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                    >
                      {tr("Mover p/ cá")}
                    </button>
                  )}
                </div>
                );
              })}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
              <span>{tr("Equipamentos da equipe")}</span>
              <span className="text-xs text-slate-500">
                {tr("Quantidade ↔ disponível")}
              </span>
            </div>
            <div className="border border-slate-200 rounded-lg p-2 max-h-44 overflow-y-auto space-y-1">
              {equipments.length === 0 && (
                <div className="text-xs text-slate-500 p-3 text-center">
                  {tr("Cadastre equipamentos no menu Equipamentos para distribuir.")}
                </div>
              )}
              {equipments.map((eq) => {
                const current = form.equipments.find(
                  (x) => x.equipment_id === eq.id,
                );
                // Quantidade já alocada por OUTRAS equipes
                const usedByOthers =
                  (teams || [])
                    .filter((t) => t.id !== item?.id)
                    .reduce((s, t) => {
                      const e = (t.equipments || []).find(
                        (x) => x.equipment_id === eq.id,
                      );
                      return s + (e ? Number(e.quantity || 0) : 0);
                    }, 0);
                const maxAvailable = Math.max(
                  0,
                  Number(eq.total_quantity || 0) - usedByOthers,
                );
                const qty = current?.quantity || 0;
                function setQty(newQty) {
                  let nq = Math.max(0, Math.min(Number(newQty) || 0, maxAvailable));
                  setForm((f) => {
                    const rest = f.equipments.filter(
                      (x) => x.equipment_id !== eq.id,
                    );
                    return {
                      ...f,
                      equipments: nq > 0
                        ? [...rest, { equipment_id: eq.id, quantity: nq }]
                        : rest,
                    };
                  });
                }
                return (
                  <div
                    key={eq.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50"
                    data-testid={`team-equipment-${eq.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate">
                        {eq.name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        Total {eq.total_quantity} · {tr("Disponível")}{" "}
                        <span
                          className={
                            maxAvailable === 0
                              ? "text-red-600 font-semibold"
                              : "text-emerald-700 font-semibold"
                          }
                        >
                          {maxAvailable}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setQty(qty - 1)}
                        disabled={qty <= 0}
                        className="w-7 h-7 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={0}
                        max={maxAvailable}
                        value={qty}
                        data-testid={`team-equipment-qty-${eq.id}`}
                        onChange={(e) => setQty(e.target.value)}
                        className="w-12 text-center border border-slate-300 rounded py-1 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setQty(qty + 1)}
                        disabled={qty >= maxAvailable}
                        className="w-7 h-7 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {tr("Cancelar")}
          </Button>
          <Button
            onClick={() => save()}
            data-testid="team-save-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {tr("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {moveClient && (
        <MoveClientDialog
          client={moveClient}
          targetTeamId={item?.id}
          targetTeamName={form.name || tr("esta equipe")}
          onClose={() => setMoveClient(null)}
          onMoved={() => {
            setMoveClient(null);
            onSaved();
          }}
        />
      )}
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TeamFormProgress — usa FormProgress shared. Garante equipe não vazia.
// ─────────────────────────────────────────────────────────────────────────
function TeamFormProgress({ form }) {
  const memberCount = Array.isArray(form.member_ids) ? form.member_ids.length : 0;
  const items = [
    { key: "name", label: "Nome", ok: !!(form.name || "").trim() },
    { key: "color", label: "Cor", ok: !!form.color },
    { key: "members", label: `Membros (${memberCount})`, ok: memberCount >= 1 },
  ];
  return <FormProgress items={items} testId="team-form-progress" />;
}



function InlineEditName({ team, onSaved, testId }) {
  const { t: tr } = useI18n();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(team.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(team.name || ""); }, [team.name]);

  async function commit() {
    const trimmed = value.trim();
    if (!trimmed) { setValue(team.name); setEditing(false); return; }
    if (trimmed === team.name) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/teams/${team.id}`, { name: trimmed });
      toast.success(tr("Equipe renomeada"));
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || tr("Falha ao renomear"));
      setValue(team.name);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setValue(team.name); setEditing(false); }
        }}
        disabled={saving}
        data-testid={`${testId}-input`}
        className="cm-input text-sm font-semibold py-1 px-2 max-w-[220px]"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={testId}
      className="hover:bg-blue-50 px-1 -mx-1 rounded transition-colors group"
      title={tr("Clique para renomear")}
    >
      {team.name}
      <Pencil
        size={11}
        className="inline ml-1.5 text-slate-400 group-hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
      />
    </button>
  );
}
